import 'reflect-metadata'

import { trace } from '@opentelemetry/api'
import { objectToCamel } from 'ts-case-convert'
import { plainToInstance, type ClassConstructor } from 'class-transformer'
import { validate } from 'class-validator'

import { ApiError, ValidationError } from './base-errors'
import { firebaseAuthClient } from 'lib/firebase/client'

export abstract class BaseApiService<K = unknown> {
  constructor() {}

  private async buildHeaders(
    options: RequestInit,
    isFormData: boolean
  ): Promise<Record<string, string>> {
    // Get Firebase auth token if user is authenticated
    let authToken: string | null = null
    try {
      const currentUser = firebaseAuthClient.currentUser
      if (currentUser) {
        authToken = await currentUser.getIdToken()
      }
    } catch {
      // User not authenticated or token retrieval failed - continue without token
    }

    // Build headers object
    const headers: Record<string, string> = {}

    // Copy existing headers if provided
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value
        })
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value
        })
      } else {
        Object.assign(headers, options.headers)
      }
    }

    // Set Content-Type for non-FormData requests
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    // Remove Content-Type for FormData - browser will set it with boundary
    if (isFormData && headers['Content-Type']) {
      delete headers['Content-Type']
    }

    // Add Authorization header if token exists
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    return headers
  }

  protected async request<T extends K>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Check if body is FormData - if so, don't set Content-Type header
    // Browser will automatically set it with boundary for FormData
    const isFormData = options.body instanceof FormData

    const headers = await this.buildHeaders(options, isFormData)

    const response = await trace
      .getTracer('tino-frontend-app')
      .startActiveSpan(url, async (span) => {
        try {
          return await fetch(url, { headers, ...options })
        } finally {
          span.end()
        }
      })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new ApiError(
        errorData.message || errorData.error || `HTTP Error ${response.status}`,
        response.status,
        errorData.code
      )
    }

    const text = await response.text()
    return text ? (JSON.parse(text) as T) : (undefined as T)
  }

  protected async validateAndTransform<T = K>(
    data: T,
    EntityClass: ClassConstructor<T>
  ): Promise<T> {
    const parsedData = objectToCamel(data as object)

    const instance = plainToInstance(EntityClass, parsedData, {
      enableImplicitConversion: true,
      excludeExtraneousValues: false,
    })

    const errors = await validate(instance as object)

    if (errors.length > 0) {
      const errorMessages = errors.flatMap((error) => {
        const constraints = error.constraints || {}
        const messages = Object.values(constraints)
        return messages.map((msg) => `${error.property}: ${msg}`)
      })

      throw new ValidationError(
        `Validation failed for ${EntityClass.name}`,
        errorMessages
      )
    }

    return instance
  }

  protected async validateAndTransformArray<T extends K>(
    data: T[],
    EntityClass: ClassConstructor<T>
  ): Promise<T[]> {
    return Promise.all(
      data.map((item) => this.validateAndTransform<T>(item, EntityClass))
    )
  }
}
